import { useState, useEffect, useMemo, useCallback } from "react";
import { watchDeals, createDeal, updateDeal, logDealActivity } from "../lib/db";
import {
  DEAL_STAGES,
  DEAL_STAGE_MAP,
  DEAL_OPEN_STAGES,
  normalizeDealStage,
} from "../lib/constants";
import { CRM_OPERATORS } from "../lib/crmUsers";

const CLOSED_STAGES = new Set(["won", "lost"]);

function dealStageLabel(stage) {
  const id = normalizeDealStage(stage);
  return DEAL_STAGE_MAP[id]?.label || stage || "—";
}

function contactShowsInterest(c) {
  if (!c || c.isInternal) return false;
  if (c.stage && c.stage !== "lead") return true;
  if ((c.totalScans || 0) > 0) return true;
  if (String(c.notes || "").trim()) return true;
  if ((c.mrr || 0) > 0 || (c.lifetimeRevenue || 0) > 0) return true;
  const tags = c.tags || [];
  if (tags.some((t) => /demo|engaged|interested|replied|call/i.test(String(t)))) return true;
  return false;
}

function activeDealContactIds(deals) {
  const ids = new Set();
  for (const d of deals) {
    if (!d.contactId) continue;
    const stage = normalizeDealStage(d.stage);
    if (!CLOSED_STAGES.has(stage)) ids.add(d.contactId);
  }
  return ids;
}

function formatValue(deal) {
  const cur = (deal.currency || "usd").toUpperCase();
  return `${cur} ${Number(deal.value || 0).toLocaleString()}`;
}

export default function Deals({ onOpenDeal, contacts = [], userEmail = "" }) {
  const [deals, setDeals] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [contactSearch, setContactSearch] = useState("");
  const [contactFilterEngaged, setContactFilterEngaged] = useState(true);
  const [form, setForm] = useState({
    title: "",
    value: "",
    stage: "lead",
    contactId: "",
    companyName: "",
    pocName: "",
    website: "",
    notes: "",
  });

  const isAdmin = CRM_OPERATORS.some(
    (o) => o.email.toLowerCase() === String(userEmail || "").toLowerCase()
  );

  useEffect(() => {
    return watchDeals(
      (rows) => {
        setLoadErr(null);
        setDeals(rows);
      },
      () => setLoadErr("Could not load deals.")
    );
  }, []);

  const dealsByStage = useMemo(() => {
    const map = Object.fromEntries(DEAL_STAGES.map((s) => [s.id, []]));
    for (const d of deals) {
      const stage = normalizeDealStage(d.stage);
      if (map[stage]) map[stage].push(d);
      else map.lead.push(d);
    }
    for (const id of Object.keys(map)) {
      map[id].sort((a, b) => {
        const ta = a.updatedAt?.toDate?.()?.getTime() || 0;
        const tb = b.updatedAt?.toDate?.()?.getTime() || 0;
        return tb - ta;
      });
    }
    return map;
  }, [deals]);

  const totalForecast = useMemo(
    () =>
      deals
        .filter((d) => DEAL_OPEN_STAGES.includes(normalizeDealStage(d.stage)))
        .reduce((s, d) => s + (Number(d.value) || 0), 0),
    [deals]
  );

  const openCount = useMemo(
    () => deals.filter((d) => DEAL_OPEN_STAGES.includes(normalizeDealStage(d.stage))).length,
    [deals]
  );

  const busyContactIds = useMemo(() => activeDealContactIds(deals), [deals]);

  const addableContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    return contacts
      .filter((c) => !c.isInternal)
      .filter((c) => !busyContactIds.has(c.id))
      .filter((c) => !contactFilterEngaged || contactShowsInterest(c))
      .filter((c) => {
        if (!q) return true;
        return (
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.companyName || "").toLowerCase().includes(q)
        );
      })
      .slice(0, 80);
  }, [contacts, contactSearch, contactFilterEngaged, busyContactIds]);

  const moveDealToStage = useCallback(async (dealId, newStage) => {
    const deal = deals.find((d) => d.id === dealId);
    if (!deal) return;
    const oldStage = normalizeDealStage(deal.stage);
    if (oldStage === newStage) return;
    await updateDeal(dealId, { stage: newStage });
    await logDealActivity(dealId, "stage_changed", `Stage: ${dealStageLabel(oldStage)} → ${dealStageLabel(newStage)}`, {
      oldStage,
      newStage,
      source: "kanban",
    });
  }, [deals]);

  function onDragStart(e, dealId) {
    e.dataTransfer.setData("text/deal-id", dealId);
    e.dataTransfer.effectAllowed = "move";
    setDraggingId(dealId);
  }

  function onDragEnd() {
    setDraggingId(null);
    setDropTarget(null);
  }

  function onDragOverColumn(e, stageId) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget(stageId);
  }

  async function onDropColumn(e, stageId) {
    e.preventDefault();
    setDropTarget(null);
    const dealId = e.dataTransfer.getData("text/deal-id");
    if (!dealId) return;
    setDraggingId(null);
    await moveDealToStage(dealId, stageId);
  }

  async function submitNew(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const contact = contacts.find((c) => c.id === form.contactId);
      await createDeal({
        title: form.title.trim(),
        value: form.value,
        stage: form.stage,
        contactId: form.contactId || null,
        contactName: contact?.name || "",
        companyName: form.companyName,
        pocName: form.pocName,
        website: form.website,
        notes: form.notes,
      });
      setForm({
        title: "",
        value: "",
        stage: "lead",
        contactId: "",
        companyName: "",
        pocName: "",
        website: "",
        notes: "",
      });
      setShowNew(false);
    } finally {
      setCreating(false);
    }
  }

  async function addContactAsLead(contact) {
    setCreating(true);
    try {
      const title = contact.companyName
        ? `${contact.companyName} — ${contact.name || contact.email}`
        : contact.name || contact.email || "New deal";
      const id = await createDeal({
        title,
        value: 0,
        stage: "lead",
        contactId: contact.id,
        contactName: contact.name || "",
        companyName: contact.companyName || "",
        pocName: contact.name || "",
        website: "",
        notes: contact.notes || "",
      });
      setShowAddContact(false);
      onOpenDeal?.(id);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="deals-page">
      {loadErr && (
        <div className="deals-alert">{loadErr}</div>
      )}

      <div className="deals-toolbar">
        <div className="card card-sm deals-stat">
          <div className="deals-stat-label">Open pipeline</div>
          <div className="deals-stat-row">
            <span className="deals-stat-value">{openCount}</span>
            <span className="deals-stat-muted">deals</span>
          </div>
        </div>
        <div className="card card-sm deals-stat">
          <div className="deals-stat-label">Forecast (open stages)</div>
          <div className="deals-stat-value deals-stat-forecast">${totalForecast.toLocaleString()}</div>
        </div>
        <div className="deals-toolbar-actions">
          {isAdmin && (
            <button type="button" className="btn" onClick={() => setShowAddContact(true)}>
              + Add contact to Lead
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => setShowNew(true)}>
            + New deal
          </button>
        </div>
      </div>

      <div className="deals-board-wrap">
        <div className="deals-board">
          {DEAL_STAGES.map((col) => {
            const colDeals = dealsByStage[col.id] || [];
            const colTotal = colDeals.reduce((s, d) => s + (Number(d.value) || 0), 0);
            const isClosed = CLOSED_STAGES.has(col.id);
            return (
              <div
                key={col.id}
                className={`deals-column ${dropTarget === col.id ? "deals-column--drop" : ""} ${isClosed ? "deals-column--closed" : ""}`}
                onDragOver={(e) => onDragOverColumn(e, col.id)}
                onDragLeave={() => setDropTarget((t) => (t === col.id ? null : t))}
                onDrop={(e) => onDropColumn(e, col.id)}
              >
                <div className="deals-column-header" style={{ borderTopColor: col.color }}>
                  <span className="deals-column-title">{col.label}</span>
                  <span className="deals-column-meta">
                    {colDeals.length}
                    {colTotal > 0 && !isClosed ? ` · $${colTotal.toLocaleString()}` : ""}
                  </span>
                </div>
                <div className="deals-column-body">
                  {colDeals.length === 0 ? (
                    <div className="deals-column-empty">Drop deals here</div>
                  ) : (
                    colDeals.map((d) => (
                      <div
                        key={d.id}
                        className={`deal-card ${draggingId === d.id ? "deal-card--dragging" : ""}`}
                        draggable
                        onDragStart={(e) => onDragStart(e, d.id)}
                        onDragEnd={onDragEnd}
                        onClick={() => onOpenDeal(d.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && onOpenDeal(d.id)}
                      >
                        <div className="deal-card-title">{d.title}</div>
                        {d.companyName ? (
                          <div className="deal-card-sub">{d.companyName}</div>
                        ) : null}
                        <div className="deal-card-footer">
                          <span className="deal-card-value">{formatValue(d)}</span>
                          {d.pocName ? <span className="deal-card-poc">{d.pocName}</span> : null}
                        </div>
                        {d.contactName ? (
                          <div className="deal-card-contact">{d.contactName}</div>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showNew && (
        <div className="modal-overlay" onClick={() => !creating && setShowNew(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div className="modal-title">New deal</div>
              <button type="button" className="btn btn-ghost btn-icon" onClick={() => setShowNew(false)} disabled={creating}>
                ×
              </button>
            </div>
            <form onSubmit={submitNew} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Title *
                <input
                  className="input"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  required
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Value (forecast)
                <input
                  className="input"
                  type="number"
                  value={form.value}
                  onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Stage
                <select
                  className="input"
                  value={form.stage}
                  onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                >
                  {DEAL_STAGES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Link contact
                <select
                  className="input"
                  value={form.contactId}
                  onChange={(e) => {
                    const contactId = e.target.value;
                    const c = contacts.find((x) => x.id === contactId);
                    setForm((f) => ({
                      ...f,
                      contactId,
                      companyName: f.companyName || c?.companyName || "",
                      pocName: f.pocName || c?.name || "",
                    }));
                  }}
                  style={{ marginTop: 4, width: "100%" }}
                >
                  <option value="">— Optional —</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.email})
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Company name
                <input
                  className="input"
                  value={form.companyName}
                  onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
                  placeholder="Acme Inc."
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                POC name
                <input
                  className="input"
                  value={form.pocName}
                  onChange={(e) => setForm((f) => ({ ...f, pocName: e.target.value }))}
                  placeholder="Point of contact"
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Website
                <input
                  className="input"
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Notes
                <textarea
                  className="textarea"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  style={{ marginTop: 4, minHeight: 72 }}
                />
              </label>
              <div className="modal-footer" style={{ marginTop: 8 }}>
                <button type="button" className="btn" onClick={() => setShowNew(false)} disabled={creating}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? "Saving…" : "Create deal"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddContact && (
        <div className="modal-overlay" onClick={() => !creating && setShowAddContact(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div className="modal-title">Add contact to Lead</div>
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                onClick={() => setShowAddContact(false)}
                disabled={creating}
              >
                ×
              </button>
            </div>
            <p style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12, lineHeight: 1.45 }}>
              Pull sign-ups who have shown interest (activity, scans, notes, or past demo stage) into the pipeline as
              Lead. Contacts already on an open deal are hidden.
            </p>
            <input
              className="input"
              placeholder="Search name, email, company…"
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              style={{ width: "100%", marginBottom: 10 }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-2)", marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={contactFilterEngaged}
                onChange={(e) => setContactFilterEngaged(e.target.checked)}
              />
              Only engaged / interested contacts
            </label>
            <div className="deals-contact-pick-list">
              {addableContacts.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>
                  No matching contacts. Try clearing the filter or search.
                </div>
              ) : (
                addableContacts.map((c) => (
                  <div key={c.id} className="deals-contact-pick-row">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{c.name || c.email}</div>
                      <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {c.email}
                        {c.companyName ? ` · ${c.companyName}` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={creating}
                      onClick={() => addContactAsLead(c)}
                    >
                      Add as Lead
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
